import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import Select from "react-select";
import { Download, RefreshCw, X } from "react-feather";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { exportPaymentsReport, fetchBranches, getPayments } from "../../utils/apiCalls";
import { saveBlob } from "../../utils/downloadBlob";
import { canExportPayments } from "../../constants/roles";
import { errorToast, successToast, loadingToast, dismissToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS, fieldLabel, dateInput, selectStyles } from "../reporting/reportingStyles";
import { DATE_PRESETS } from "../reporting/reportingFilters";
import { RCard, Loader as RLoader } from "../reporting/ReportingShared";

// The report's server-side cap on the period (PaymentReport maxSpanDays).
const REPORT_MAX_DAYS = 92;
// "This Year" and "All Time" would exceed the cap.
const REPORT_PRESETS = DATE_PRESETS.filter((p) => p.value !== "all" && p.value !== "year");
const spanDays = (start, end) => Math.round((new Date(end) - new Date(start)) / 864e5) + 1;

const STATUS_COLOR = {
  paid: { bg: C.greenLight, fg: C.green },
  refunded: { bg: C.blueLight, fg: C.blue },
};

const RECON_COLOR = {
  reconciled: { bg: C.greenLight, fg: C.green },
  pending: { bg: C.orangeLight, fg: C.orange },
  orphaned: { bg: C.coralLight, fg: C.coral },
};

// Gateway is not a status, so it gets a neutral outline rather than a traffic-light
// colour — it says "who took the money", not "is this healthy".
//
// One hue per gateway, and Moyasar keeps the neutral: it is the default and the bulk of the rows, so
// colouring it would tint the whole table and leave nothing standing out.
const GATEWAY_HUES = {
  telr: { fg: C.blue, bg: C.blueLight },
  nearpay: { fg: C.purple, bg: C.purpleLight },
};

const BRAND_LABEL = {
  visa: "Visa",
  mastercard: "Mastercard",
  mada: "mada",
  amex: "Amex",
  maestro: "Maestro",
};

// "Visa ••1111". The columns are written server-side from the gateway's own record.
const cardLabel = (p) => {
  if (!p?.cardBrand && !p?.cardLast4) return null;
  const brand = p.cardBrand ? BRAND_LABEL[p.cardBrand] || p.cardBrand : "Card";
  return p.cardLast4 ? `${brand} ••${p.cardLast4}` : brand;
};

/** What `moyasarPaymentId` actually holds, per gateway — see the detail drawer. */
const GATEWAY_REF_LABEL = {
  telr: "Telr Order Ref",
  nearpay: "NearPay Transaction",
};

const GATEWAY_PILL = (gateway) => {
  const hue = GATEWAY_HUES[gateway];
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "capitalize",
    border: `1px solid ${hue ? hue.fg : C.borderLight}`,
    color: hue ? hue.fg : C.textMed,
    background: hue ? hue.bg : C.bg,
  };
};

// Mirrors the server's readTelrTransactionRef: the Order API exposes it as
// payments[].reference while the hosted page uses transaction.ref.
const telrTransactionRef = (payment) => {
  const raw = payment?.rawResponse;
  if (!raw) return null;
  const list = Array.isArray(raw.payments) ? raw.payments : [];
  const last = list.length ? list[list.length - 1] : null;
  const t = last || raw.transaction || null;
  if (!t) return null;
  return t.ref ?? t.transactionRef ?? t.reference ?? t.id ?? null;
};

const Pill = ({ value, palette }) => {
  const c = palette[value] || { bg: C.bg, fg: C.textMed };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: c.bg,
        color: c.fg,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.fg }} />
      {(value || "").replace(/_/g, " ").toUpperCase()}
    </span>
  );
};

const filterSelectStyle = {
  padding: "7px 12px",
  fontSize: 12,
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  fontFamily: "inherit",
  outline: "none",
  color: C.text,
  background: C.white,
  cursor: "pointer",
  minWidth: 140,
};

const Payments = () => {
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector(
    (state) => state.merchant || {}
  );
  const selectedMerchant =
    userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [isLoading, setIsLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [detail, setDetail] = useState(null);

  const [filters, setFilters] = useState({
    MerchantId: selectedMerchant || "",
    status: "",
    reconciliationStatus: "",
    source: "",
    reconciledBy: "",
    gateway: "",
    search: "",
    userId: "",
    startDate: "",
    endDate: "",
    branchIds: [],
    limit: 20,
  });

  const { merchants = [] } = useSelector((state) => state.merchant || {});
  const canExport = canExportPayments(userType);
  const merchantChosen = !!selectedMerchant && selectedMerchant !== "all";
  const [branches, setBranches] = useState([]);

  const [reportOpen, setReportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [report, setReport] = useState({
    preset: "month",
    startDate: "",
    endDate: "",
    branchIds: [],
  });

  const fetchPayments = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      const data = await getPayments(page, filters.limit, filters);
      if (data) {
        setPayments(data.rows || []);
        setTotalPages(data.totalPages || 1);
      } else {
        setPayments([]);
        setTotalPages(1);
      }
      setIsLoading(false);
    },
    [filters]
  );

  useEffect(() => {
    fetchPayments(currentPage);
  }, [fetchPayments, currentPage]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, MerchantId: selectedMerchant || "", branchIds: [] }));
    setCurrentPage(1);
  }, [selectedMerchant]);

  // Branches only make sense inside one merchant; across all merchants the filter is hidden.
  useEffect(() => {
    if (!merchantChosen) {
      setBranches([]);
      return;
    }
    let cancelled = false;
    fetchBranches({ merchantId: selectedMerchant }, false)
      .then((res) => {
        if (!cancelled) setBranches((res?.data || []).filter((b) => String(b.MerchantId) === String(selectedMerchant)));
      })
      .catch(() => !cancelled && setBranches([]));
    return () => {
      cancelled = true;
    };
  }, [merchantChosen, selectedMerchant]);

  const branchOptions = useMemo(() => branches.map((b) => ({ value: b.id, label: b.name })), [branches]);
  const branchValue = (ids) => branchOptions.filter((o) => ids.includes(o.value));

  const openReport = () => {
    // Start from the page's own filters, so the file matches what is on screen.
    const hasPageDates = filters.startDate && filters.endDate;
    const [start, end] = hasPageDates
      ? [filters.startDate, filters.endDate]
      : REPORT_PRESETS.find((p) => p.value === "month").range();
    setReport((prev) => ({
      ...prev,
      preset: hasPageDates ? "custom" : "month",
      startDate: start,
      endDate: end,
      branchIds: filters.branchIds,
    }));
    setReportOpen(true);
  };

  const reportError = !report.startDate || !report.endDate
    ? "Choose a start and end date."
    : report.startDate > report.endDate
      ? "The start date is after the end date."
      : spanDays(report.startDate, report.endDate) > REPORT_MAX_DAYS
        ? `The period can be at most ${REPORT_MAX_DAYS} days.`
        : null;

  const reportFilename = () => {
    const slug = (v) =>
      (v || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
    const name = merchantChosen ? merchants.find((m) => String(m.id) === String(selectedMerchant))?.name : null;
    const scope = merchantChosen ? slug(name) || `merchant-${selectedMerchant}` : "all-merchants";
    return `payments-report-${scope}-${report.startDate}_${report.endDate}.xlsx`;
  };

  const handleExportReport = async () => {
    if (reportError) return;
    setIsExporting(true);
    const toastId = loadingToast("Preparing report…");
    try {
      const { blob, totalCount } = await exportPaymentsReport({
        MerchantId: selectedMerchant,
        startDate: report.startDate,
        endDate: report.endDate,
        branchIds: merchantChosen ? report.branchIds : [],
      });
      saveBlob(blob, reportFilename());
      successToast(
        totalCount != null ? `Report ready — ${totalCount} payment${totalCount === 1 ? "" : "s"}` : "Report ready"
      );
      setReportOpen(false);
    } catch (err) {
      errorToast(err?.message || "Export failed.");
    } finally {
      dismissToast(toastId);
      setIsExporting(false);
    }
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const customerName = (user) =>
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.phoneNo ||
    user?.email ||
    "—";

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1).slice(
    Math.max(0, currentPage - 3),
    Math.min(totalPages, currentPage + 2)
  );

  return (
    <Fragment>
      <Breadcrumb title="Payments" parent="Orders" />
      <Container fluid={true}>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          <RCard>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              <div
                onClick={() => fetchPayments(currentPage)}
                title="Refresh"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  border: `1.5px solid ${C.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  background: C.white,
                  flexShrink: 0,
                }}
              >
                <RefreshCw size={15} color={C.textMed} />
              </div>

              <input
                placeholder="Search payment ref / external id"
                value={filters.search}
                onChange={(e) => {
                  setFilters({ ...filters, search: e.target.value });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setCurrentPage(1);
                    fetchPayments(1);
                  }
                }}
                style={{ ...filterSelectStyle, minWidth: 240, cursor: "text" }}
              />

              <input
                type="text"
                inputMode="numeric"
                placeholder="User ID"
                value={filters.userId}
                onChange={(e) => {
                  // digits only — the id is numeric
                  setFilters({ ...filters, userId: e.target.value.replace(/\D/g, "") });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setCurrentPage(1);
                    fetchPayments(1);
                  }
                }}
                style={{ ...filterSelectStyle, minWidth: 120, cursor: "text" }}
              />

              <select
                value={filters.status}
                onChange={(e) => {
                  setFilters({ ...filters, status: e.target.value });
                  setCurrentPage(1);
                }}
                style={filterSelectStyle}
              >
                <option value="">All statuses</option>
                <option value="paid">Paid</option>
                <option value="refunded">Refunded</option>
              </select>

              <select
                value={filters.reconciliationStatus}
                onChange={(e) => {
                  setFilters({ ...filters, reconciliationStatus: e.target.value });
                  setCurrentPage(1);
                }}
                style={filterSelectStyle}
              >
                <option value="">All reconciliation</option>
                <option value="reconciled">Reconciled</option>
                <option value="pending">Pending</option>
                <option value="orphaned">Orphaned</option>
              </select>

              <select
                value={filters.source}
                onChange={(e) => {
                  setFilters({ ...filters, source: e.target.value });
                  setCurrentPage(1);
                }}
                style={filterSelectStyle}
              >
                <option value="">All sources</option>
                <option value="order_flow">Order flow</option>
                <option value="webhook">Webhook</option>
                {/* Charges the server found for itself because no webhook arrived. A rising count
                    here is the signal that the webhook is broken — without this filter the backstop
                    covers for it invisibly. */}
                <option value="sweep">Sweep (webhook missed)</option>
              </select>

              <select
                value={filters.gateway}
                onChange={(e) => {
                  setFilters({ ...filters, gateway: e.target.value });
                  setCurrentPage(1);
                }}
                style={filterSelectStyle}
              >
                <option value="">All gateways</option>
                <option value="moyasar">Moyasar</option>
                <option value="telr">Telr</option>
                {/* A card taken on a screen's own NearPay reader. Not a checkout gateway a merchant
                    picks — it appears here because a terminal capture is money like any other, and
                    the ledger has to be able to show it. */}
                <option value="nearpay">NearPay</option>
              </select>

              <select
                value={filters.reconciledBy}
                onChange={(e) => {
                  setFilters({ ...filters, reconciledBy: e.target.value });
                  setCurrentPage(1);
                }}
                style={filterSelectStyle}
              >
                <option value="">All recovery</option>
                <option value="order_flow">Order flow</option>
                <option value="draft_autocreate">Draft auto-create</option>
                <option value="order_link">Order link</option>
              </select>

              <input
                type="date"
                aria-label="From date"
                title="Paid from"
                value={filters.startDate}
                max={filters.endDate || undefined}
                onChange={(e) => {
                  setFilters({ ...filters, startDate: e.target.value });
                  setCurrentPage(1);
                }}
                style={dateInput}
              />
              <input
                type="date"
                aria-label="To date"
                title="Paid to"
                value={filters.endDate}
                min={filters.startDate || undefined}
                onChange={(e) => {
                  setFilters({ ...filters, endDate: e.target.value });
                  setCurrentPage(1);
                }}
                style={dateInput}
              />

              {merchantChosen && (
                <div style={{ minWidth: 220 }}>
                  <Select
                    isMulti
                    aria-label="Branches"
                    options={branchOptions}
                    value={branchValue(filters.branchIds)}
                    onChange={(val) => {
                      setFilters({ ...filters, branchIds: (val || []).map((v) => v.value) });
                      setCurrentPage(1);
                    }}
                    placeholder="All branches"
                    styles={selectStyles}
                    closeMenuOnSelect={false}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  />
                </div>
              )}

              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 12,
                  color: C.textLight,
                  background: C.bg,
                  padding: "4px 12px",
                  borderRadius: 20,
                }}
              >
                {payments?.length || 0} payments
              </span>

              {canExport && (
                <Button
                  size="sm"
                  onClick={openReport}
                  disabled={isExporting}
                  title="Download totals by branch, channel, app, gateway, method and card scheme"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    borderRadius: 8,
                    background: C.white,
                    color: C.text,
                    border: `1.5px solid ${C.border}`,
                    fontWeight: 600,
                    fontSize: 12,
                    opacity: isExporting ? 0.55 : 1,
                  }}
                >
                  <Download size={14} />
                  {isExporting ? "Exporting…" : "Export Report"}
                </Button>
              )}
            </div>

            {isLoading ? (
              <RLoader />
            ) : (
              <div
                style={{
                  overflowX: "auto",
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                }}
              >
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={tbl.th}>ID</th>
                      <th style={tbl.th}>Created</th>
                      <th style={tbl.th}>Payment Ref</th>
                      <th style={tbl.th}>Gateway</th>
                      <th style={tbl.th}>Card</th>
                      <th style={tbl.th}>Amount</th>
                      <th style={tbl.th}>Customer</th>
                      <th style={tbl.th}>Branch</th>
                      <th style={tbl.th}>Order</th>
                      <th style={tbl.th}>Source</th>
                      <th style={tbl.th}>Reconciled By</th>
                      <th style={tbl.th}>Reconciliation</th>
                      <th style={tbl.th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments?.length > 0 ? (
                      payments.map((p) => (
                        <tr
                          key={p.id}
                          className="rpt-row"
                          style={{ cursor: "pointer" }}
                          onClick={() => setDetail(p)}
                        >
                          <td style={{ ...tbl.td, color: C.coral, fontWeight: 700 }}>#{p.id}</td>
                          <td style={tbl.td}>{formatDate(p.createdAt)}</td>
                          <td
                            style={{ ...tbl.td, fontWeight: 600, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            title={p.moyasarPaymentId}
                          >
                            {p.moyasarPaymentId}
                          </td>
                          <td style={tbl.td}>
                            <span style={GATEWAY_PILL(p.gateway)}>{p.gateway || "moyasar"}</span>
                          </td>
                          <td style={tbl.td}>{cardLabel(p) || "—"}</td>
                          <td style={tbl.td}>
                            {p.amount ?? "—"} {p.currency ?? ""}
                          </td>
                          <td style={tbl.td}>{customerName(p.User)}</td>
                          <td style={tbl.td}>{p.Branch?.name || "—"}</td>
                          <td style={tbl.td}>{p.OrderId ? `#${p.OrderId}` : "—"}</td>
                          <td style={{ ...tbl.td, color: C.textMed }}>
                            {(p.source || "").replace(/_/g, " ")}
                          </td>
                          <td style={{ ...tbl.td, color: C.textMed }}>
                            {p.reconciledBy ? p.reconciledBy.replace(/_/g, " ") : "—"}
                          </td>
                          <td style={tbl.td}>
                            <Pill value={p.reconciliationStatus} palette={RECON_COLOR} />
                          </td>
                          <td style={tbl.td}>
                            <Pill value={p.status} palette={STATUS_COLOR} />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="13"
                          style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}
                        >
                          No payments found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {payments?.length > 0 && totalPages > 1 && (
              <Pagination className="d-flex justify-content-end mt-3 mb-0">
                <PaginationItem disabled={currentPage <= 1}>
                  <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                </PaginationItem>
                {pageNumbers.map((i) => (
                  <PaginationItem active={i === currentPage} key={i}>
                    <PaginationLink onClick={() => handlePageChange(i)}>{i}</PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem disabled={currentPage >= totalPages}>
                  <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                </PaginationItem>
              </Pagination>
            )}
          </RCard>
        </div>

        <PaymentDetailModal payment={detail} onClose={() => setDetail(null)} customerName={customerName} />

        <Modal isOpen={reportOpen} toggle={() => !isExporting && setReportOpen(false)} size="lg">
          <ModalHeader
            toggle={() => !isExporting && setReportOpen(false)}
            style={{ borderBottom: `2px solid ${C.borderLight}` }}
          >
            <span style={{ fontWeight: 700, color: C.text }}>Export payments report</span>
          </ModalHeader>
          <ModalBody style={{ padding: 24, background: C.bg }}>
            <div style={{ fontSize: 13, color: C.textMed, marginBottom: 16 }}>
              An Excel file with totals by branch, platform (sales channel, app, payment gateway), payment
              method (card, Apple Pay, Google Pay, in-app wallet…) and card scheme (mada, Visa, Mastercard…) —{" "}
              {merchantChosen
                ? merchants.find((m) => String(m.id) === String(selectedMerchant))?.name || "selected merchant"
                : "all merchants"}
              . Wallet top-ups are listed separately and are not counted in the totals. A Payment Lines sheet
              lists every payment with its order, branch, customer and card details.
            </div>

            <div style={fieldLabel}>Period</div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
              {REPORT_PRESETS.map((p) => (
                <div
                  key={p.value}
                  role="button"
                  onClick={() => {
                    const [startDate, endDate] = p.range();
                    setReport((prev) => ({ ...prev, preset: p.value, startDate, endDate }));
                  }}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: report.preset === p.value ? 700 : 500,
                    cursor: "pointer",
                    border: `1.5px solid ${report.preset === p.value ? C.coral : C.border}`,
                    background: report.preset === p.value ? C.coralLight : C.white,
                    color: report.preset === p.value ? C.coral : C.textMed,
                    whiteSpace: "nowrap",
                  }}
                >
                  {p.label}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              <div>
                <div style={fieldLabel}>From</div>
                <input
                  type="date"
                  aria-label="Report from"
                  value={report.startDate}
                  onChange={(e) => setReport({ ...report, startDate: e.target.value, preset: "custom" })}
                  style={dateInput}
                />
              </div>
              <div>
                <div style={fieldLabel}>To</div>
                <input
                  type="date"
                  aria-label="Report to"
                  value={report.endDate}
                  onChange={(e) => setReport({ ...report, endDate: e.target.value, preset: "custom" })}
                  style={dateInput}
                />
              </div>
            </div>

            {merchantChosen && (
              <div style={{ marginBottom: 16 }}>
                <div style={fieldLabel}>Branches</div>
                <Select
                  isMulti
                  aria-label="Report branches"
                  options={branchOptions}
                  value={branchValue(report.branchIds)}
                  onChange={(val) => setReport({ ...report, branchIds: (val || []).map((v) => v.value) })}
                  placeholder="All branches"
                  styles={selectStyles}
                  closeMenuOnSelect={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                />
              </div>
            )}

            {reportError && (
              <div style={{ fontSize: 12, color: C.orange, fontWeight: 600 }}>{reportError}</div>
            )}
          </ModalBody>
          <ModalFooter style={{ borderTop: `1px solid ${C.borderLight}` }}>
            <Button color="secondary" disabled={isExporting} onClick={() => setReportOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={isExporting || !!reportError}
              onClick={handleExportReport}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                border: "none",
                borderRadius: 8,
                padding: "8px 20px",
                fontWeight: 600,
              }}
            >
              <Download size={14} />
              {isExporting ? "Exporting…" : "Download Excel"}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </Fragment>
  );
};

const DetailRow = ({ label, value, mono }) => (
  <div style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: `1px solid ${C.borderLight}` }}>
    <div style={{ flex: "0 0 150px", fontSize: 12, fontWeight: 600, color: C.textMed }}>{label}</div>
    <div
      style={{
        flex: 1,
        fontSize: 13,
        color: C.text,
        wordBreak: "break-word",
        fontFamily: mono ? "monospace" : "inherit",
      }}
    >
      {value === null || value === undefined || value === "" ? "—" : value}
    </div>
  </div>
);

const JsonBlock = ({ data }) => (
  <pre
    style={{
      margin: 0,
      padding: 12,
      background: C.bg,
      border: `1px solid ${C.border}`,
      borderRadius: 8,
      fontSize: 11,
      lineHeight: 1.5,
      color: C.text,
      maxHeight: 240,
      overflow: "auto",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    }}
  >
    {(() => {
      try {
        return JSON.stringify(typeof data === "string" ? JSON.parse(data) : data, null, 2);
      } catch (e) {
        return String(data);
      }
    })()}
  </pre>
);

const SectionTitle = ({ title }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: C.coral,
      marginTop: 18,
      marginBottom: 4,
    }}
  >
    {title}
  </div>
);

const PaymentDetailModal = ({ payment, onClose, customerName }) => {
  if (!payment) return null;
  return (
    <Modal isOpen={!!payment} toggle={onClose} centered size="lg">
      <style>{reportingGlobalCSS}</style>
      <ModalBody style={{ padding: 0 }} className="rpt-wrap">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 22px",
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Payment #{payment.id}</span>
            <Pill value={payment.status} palette={STATUS_COLOR} />
            <Pill value={payment.reconciliationStatus} palette={RECON_COLOR} />
          </div>
          <div onClick={onClose} style={{ cursor: "pointer", color: C.textMed, display: "flex" }} title="Close">
            <X size={20} />
          </div>
        </div>

        <div style={{ padding: "12px 22px 22px" }}>
          <SectionTitle title="Payment" />
          <DetailRow label="Gateway" value={payment.gateway || "moyasar"} />
          <DetailRow
            // The column is `moyasarPaymentId` for every gateway, but what it HOLDS differs, and the
            // label is what tells someone what to quote to whom. For NearPay it is the
            // `transaction_uuid` — the id to search in NearPay's own dashboard.
            label={GATEWAY_REF_LABEL[payment.gateway] ?? "Payment Ref"}
            value={payment.moyasarPaymentId}
            mono
          />
          {payment.gateway === "telr" && (
            // The ref a refund is actually issued against — different from the order
            // ref above, and the thing to quote to Telr support.
            <DetailRow
              label="Transaction Ref"
              value={telrTransactionRef(payment) ?? "—"}
              mono
            />
          )}
          <DetailRow label="Amount" value={`${payment.amount ?? "—"} ${payment.currency ?? ""}`} />
          <DetailRow label="Source type" value={payment.sourceType} />
          <DetailRow label="Card" value={cardLabel(payment)} />
          <DetailRow label="Recorded via" value={(payment.source || "").replace(/_/g, " ")} />
          <DetailRow label="Created" value={formatDate(payment.createdAt)} />
          <DetailRow label="Paid at" value={payment.paidAt ? formatDate(payment.paidAt) : null} />

          <SectionTitle title="Order" />
          <DetailRow
            label="Order ID"
            value={payment.OrderId ? <span style={{ fontWeight: 700, color: C.green }}>#{payment.OrderId}</span> : null}
          />
          <DetailRow label="External ID" value={payment.externalId} mono />
          <DetailRow label="Reconciled by" value={payment.reconciledBy ? payment.reconciledBy.replace(/_/g, " ") : null} />
          <DetailRow label="Reconciled at" value={payment.reconciledAt ? formatDate(payment.reconciledAt) : null} />

          <SectionTitle title="Customer" />
          <DetailRow label="Customer" value={customerName(payment.User)} />
          <DetailRow label="Phone" value={payment.User?.phoneNo} />
          <DetailRow label="Branch" value={payment.Branch?.name} />

          {payment.refundInfo ? (
            <>
              <SectionTitle title="Refund" />
              <DetailRow label="Refund status" value={payment.refundStatus} />
              <JsonBlock data={payment.refundInfo} />
            </>
          ) : null}

          {payment.rawResponse ? (
            <>
              <SectionTitle title="Raw payment" />
              <JsonBlock data={payment.rawResponse} />
            </>
          ) : null}
        </div>
      </ModalBody>
    </Modal>
  );
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "—";
  return (
    date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
    ", " +
    date.toLocaleTimeString("en-US", { hour: "numeric", minute: "numeric", hour12: true })
  );
};

export default Payments;
